/**
 * Reine Ranking-Funktion: LV-Position → passende Großhandels-Katalogartikel
 * (Softecs DATANORM-Katalog, gelesen via server/routes/softec-db.ts).
 *
 * Kaskade (härteste Signale zuerst, wie shared/gaeb-price-match.ts):
 * 1. Exakte (normalisierte) Artikelnummer- ODER EAN-Übereinstimmung
 *    → confidence 1, zone "high", deterministisch.
 *    Die Position hat kein eigenes EAN-Feld — beim Scannen eines Barcodes landet
 *    der Wert typischerweise im "artikelnummer"-Feld, daher wird dieses eine
 *    Eingabefeld gegen BEIDE Kandidatenfelder (articleNumber und ean) geprüft.
 * 2. Fuzzy-Match auf Beschreibungstext (Jaccard-Token-Overlap), im selben
 *    Normalisierungs-/Tokenizer-Stil wie Softecs
 *    lib/services/supplier-quote-matching.ts (dort: jaccardScore/normalizeDescription).
 *
 * Einheiten-Veto: Position und Kandidat werden je auf eine kanonische Alias-Gruppe
 * (Länge/Fläche/Volumen-je-Skala/Gewicht-je-Skala/Stück/je EIN Verpackungstyp)
 * abgebildet. Sind beide Gruppen bekannt und unterschiedlich, ist der Kandidat
 * konservativ falsch (z.B. "m" vs. "Stk", aber auch "Rolle" vs. "Karton" — siehe
 * P1-2) und wird verworfen — NICHT wenn eine Seite unbekannt ist ("unbekannt
 * ≠ Veto", vgl. CLAUDE.md Prinzip 8: Bestandsdaten/Zahlen nie leichtfertig verwerfen).
 *
 * Konfidenz-Zonen (Schwellen bewusst konservativ gewählt, dokumentiert):
 *   high   >= 0.85
 *   medium >= 0.6
 *   low    < 0.6
 * "low" wird zurückgegeben (Transparenz), aber NIE mit einem Auto-Select-Flag
 * versehen — die Übernahme in eine Position bleibt immer eine bewusste
 * Nutzerentscheidung für alles außer eindeutigen harten Treffern.
 */

// ============================================
// Types
// ============================================

export interface PositionForCatalogMatch {
  name: string;
  beschreibung?: string | null;
  einheit?: string | null;
  artikelnummer?: string | null;
}

export interface CatalogArticleCandidate {
  id: string;
  articleNumber?: string | null;
  ean?: string | null;
  description: string;
  descriptionLong?: string | null;
  unit?: string | null;
  price?: number | null;
}

export type CatalogMatchMethod = 'articleNumber' | 'ean' | 'fuzzy';
export type CatalogMatchZone = 'high' | 'medium' | 'low';

export interface RankedCatalogCandidate {
  candidate: CatalogArticleCandidate;
  confidence: number; // 0-1
  zone: CatalogMatchZone;
  method: CatalogMatchMethod;
}

// ============================================
// Confidence zones
// ============================================

const HIGH_THRESHOLD = 0.85;
const MEDIUM_THRESHOLD = 0.6;
const DEFAULT_TOP_N = 5;

function zoneForConfidence(confidence: number): CatalogMatchZone {
  if (confidence >= HIGH_THRESHOLD) return 'high';
  if (confidence >= MEDIUM_THRESHOLD) return 'medium';
  return 'low';
}

// ============================================
// Code normalization (Artikelnummer/EAN) — Stil wie
// Softecs lib/services/supplier-quote-matching.ts::normalizeArticleNumber
// ============================================

function present(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

// Hinweis: bewusst OHNE Unicode-Property-Escapes (\p{L}) / "u"-Flag — dieses
// Repo's tsconfig hat kein explizites "target" (< ES2015), unter dem `tsc`
// den "u"-Flag ablehnt (TS1501). Softecs Vorbild-Datei nutzt \p{L}\p{N} unter
// einem höheren target; hier reicht eine explizite de-DE-Zeichenklasse.
/**
 * P2-2 (Fund, Codex-Review gpt-5.6): exportiert, damit server/routes/softec-db.ts dieselbe
 * Normalisierung für ein zusätzliches SQL-Vorfilter-Kriterium wiederverwenden kann (siehe
 * dort: buildNormalizedIdentifierMatch). Ohne diesen gemeinsamen Normalisierer würde die
 * Route ihre eigene, potenziell abweichende Regel implementieren — genau die Art von
 * Drift, die den Fund erst ausgelöst hat (Ranker normalisiert "AB123"/"AB-123" auf denselben
 * Wert, die SQL-WHERE kannte diese Äquivalenz aber nicht und filterte den Kandidaten vorher
 * schon aus).
 */
export function normalizeCode(value: string): string {
  return value
    .normalize('NFKC')
    .toLocaleLowerCase('de-DE')
    .replace(/[^a-z0-9äöüß]/g, '');
}

// ============================================
// Description fuzzy matching — Stil wie
// Softecs lib/services/supplier-quote-matching.ts::jaccardScore
// ============================================

/**
 * P2-2 (Fund, Codex-Review gpt-5.6, neue Runde): exportiert, damit server/routes/softec-db.ts
 * denselben Beschreibungstext-Normalisierer für den SQL-Text-Prefilter wiederverwenden kann
 * (siehe dort: buildNormalizedTextTokenMatch). Behandelt Interpunktion als WORTTRENNER (nicht
 * entfernt wie normalizeCode, sondern durch EIN Leerzeichen ersetzt) — "NYM-J" und "NYM J" bzw.
 * "3x1,5" und "3x1.5" normalisieren dadurch auf denselben Text. Ohne diese geteilte Normalisierung
 * würde der SQL-Prefilter (rohes ILIKE) Interpunktions-Varianten verwerfen, BEVOR der
 * nachgelagerte Fuzzy-Ranker (rankCatalogCandidates, der genau diese Normalisierung intern
 * bereits nutzt) die Zeile je zu Gesicht bekäme.
 */
export function normalizeDescription(value: string): string {
  return value
    .toLocaleLowerCase('de-DE')
    .replace(/[^a-z0-9äöüß]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function descriptionTokens(normalized: string): Set<string> {
  return new Set(normalized.length > 0 ? normalized.split(' ') : []);
}

function jaccardScore(left: string, right: string): number {
  const normalizedLeft = normalizeDescription(left);
  const normalizedRight = normalizeDescription(right);
  if (normalizedLeft.length === 0 || normalizedRight.length === 0) {
    return 0;
  }
  if (normalizedLeft === normalizedRight) {
    return 1;
  }

  const leftTokens = descriptionTokens(normalizedLeft);
  const rightTokens = descriptionTokens(normalizedRight);
  let intersectionSize = 0;
  for (const token of leftTokens) {
    if (rightTokens.has(token)) intersectionSize += 1;
  }

  const unionSize = new Set([...leftTokens, ...rightTokens]).size;
  return unionSize === 0 ? 0 : intersectionSize / unionSize;
}

/**
 * P1-D (Fund): `name` (Kurztext) und `beschreibung` (lange Spezifikation) wurden hier
 * früher zu EINEM String konkateniert, bevor gegen die Kandidatenfelder gescort wurde.
 * Die Langtext-Tokens blähten dabei die Jaccard-Union auf und drückten einen exakten
 * Kurztext-Treffer ("Steckdose" === "Steckdose") unter die high-Schwelle, sobald eine
 * lange Beschreibung mit vielen zusätzlichen Tokens dranhing — der Batch-Modus
 * preselektierte dadurch normale GAEB-Positionen (Kurztext + Langtext) nicht.
 *
 * Fix: `name` und `beschreibung` je EINZELN gegen jedes Kandidatenfeld (description/
 * descriptionLong) scoren und das beste Ergebnis nehmen. Ein exakter Kurztext-Treffer
 * bleibt dadurch unverwässert hoch, unabhängig davon, was im Langtext steht — und ein
 * Langtext-Treffer bleibt weiterhin möglich, wenn der Kurztext selbst nicht aussagekräftig
 * ist (z.B. nur eine Positionsnummer).
 */
function fuzzyScore(position: PositionForCatalogMatch, candidate: CatalogArticleCandidate): number {
  const candidateTexts = [candidate.description, candidate.descriptionLong].filter(present);
  if (candidateTexts.length === 0) return 0;

  const scoreAgainstCandidateTexts = (positionText: string | null | undefined): number => {
    if (!present(positionText)) return 0;
    return Math.max(...candidateTexts.map((text) => jaccardScore(positionText, text)));
  };

  const nameScore = scoreAgainstCandidateTexts(position.name);
  const beschreibungScore = scoreAgainstCandidateTexts(position.beschreibung);
  return Math.max(nameScore, beschreibungScore);
}

// ============================================
// Unit compatibility (Einheiten-Veto)
// ============================================

/**
 * Löst eine Preiseinheit-Schreibweise mit optionaler führender Mengenangabe auf, z.B.
 * "100 ST" → { n: 100, rest: "ST" }, "Rolle" → { n: 1, rest: "Rolle" } (kein führendes
 * Zahlenformat ⇒ N=1, ganzer Text bleibt der Rest). Identische Parse-Logik wie
 * effective-unit-price.ts::parsePriceUnit — HIER als einzige Quelle definiert (jene Datei
 * importiert diese Funktion), damit beide Stellen garantiert dasselbe Verhalten zeigen
 * statt zweier separat gepflegter Regex-Kopien, die auseinanderdriften könnten.
 *
 * P1-1 (Fund): der Fixed-Width-DATANORM-Import kann `quantityUnit` (die Einheit eines
 * Katalog-Kandidaten) als unverarbeiteten Preiseinheit-String liefern, z.B. "100 ST" statt
 * "ST". Ohne dieses Abstreifen VOR der Einheiten-Klassifikation (siehe normalizeUnitClass
 * unten) fand der Tabellen-Lookup "100 st" nicht, klassifizierte die Einheit also als
 * "unbekannt" statt "Stück" — der Veto griff dadurch NICHT, und ein 100-Stück-Katalogpreis
 * konnte als €/m in eine Meterware-Position importiert werden.
 */
export function parseLeadingQuantityPrefix(value: string): { n: number; rest: string | null } {
  const trimmed = value.trim();
  if (trimmed === '') return { n: 1, rest: null };
  const match = trimmed.match(/^(\d+(?:[.,]\d+)?)\s*(.*)$/);
  if (!match) return { n: 1, rest: trimmed };
  const parsedN = Number(match[1].replace(',', '.'));
  const n = Number.isFinite(parsedN) && parsedN > 0 ? parsedN : 1;
  const rest = match[2].trim();
  return { n, rest: rest === '' ? null : rest };
}

/**
 * Kanonische Alias-Gruppen: jede Gruppe enthält NUR echte Synonyme derselben Einheit.
 *
 * P1-2 (Fund, Modellwechsel): das vorige Modell klassifizierte grob nach "Klasse"
 * (Länge/Fläche/Volumen/Gewicht/Stück/Gebinde) und verglich NUR für Gewicht/Volumen
 * zusätzlich eine separate Skalen-Tabelle (kg vs. t korrekt verworfen) — alle
 * Verpackungsarten (Rolle, Karton, Palette, Sack, Gebinde, Paket, Set, Paar, Eimer,
 * Kanister, Flasche, Dose) landeten aber in EINER gemeinsamen Klasse "packaging" und
 * blieben dadurch fälschlich kompatibel (kein Veto zwischen Rolle und Karton, obwohl es
 * dafür keinerlei Umrechnung gibt) — ein Kartonpreis konnte so still als Pro-Rolle-EK
 * durchgehen. Fix: EIN einheitliches Modell — "gleiche kanonische Einheit" statt "gleiche
 * Klasse". Jede Verpackungsart bekommt eine EIGENE Gruppen-ID (keine Umrechnung zwischen
 * ihnen); Gewicht/Volumen behalten je eine Gruppe PRO Skala (g/kg/t, l/m³) — identisch zum
 * bisherigen Skalen-Veto, jetzt aber im selben Modell wie alles andere statt als separater
 * Sonderfall obendrauf.
 */
const UNIT_ALIAS_GROUPS: Record<string, string[]> = {
  // Länge
  laenge: ['m', 'lfm', 'lfdm', 'laufmeter', 'meter'],
  // Fläche
  flaeche: ['m2', 'm²', 'qm', 'quadratmeter'],
  // Volumen — je Skala eine eigene Gruppe (Faktor 1000 zueinander, keine Umrechnung)
  liter: ['l', 'ltr', 'liter'],
  kubikmeter: ['m3', 'm³', 'cbm', 'kubikmeter'],
  // Gewicht — je Skala eine eigene Gruppe (Faktor 1000 zueinander, keine Umrechnung)
  gramm: ['g', 'gramm'],
  kilogramm: ['kg', 'kilogramm'],
  tonne: ['t', 'to', 'tonne'],
  // Stück
  stueck: ['st', 'stk', 'stck', 'stueck', 'stück'],
  // Verpackung/Gebinde — JEDER Typ eine EIGENE Gruppe, keine Umrechnung zwischen ihnen
  gebinde: ['geb', 'gebinde'],
  packung: ['pck', 'pack', 'packung'],
  karton: ['kt', 'karton'],
  rolle: ['rolle', 'ro'],
  palette: ['pal', 'palette'],
  sack: ['sack'],
  paket: ['pak', 'paket'],
  set: ['set'],
  paar: ['paar', 'paa'],
  eimer: ['eimer'],
  kanister: ['kanister'],
  flasche: ['flasche'],
  dose: ['dose'],
};

const UNIT_ALIAS_LOOKUP: Record<string, string> = Object.fromEntries(
  Object.entries(UNIT_ALIAS_GROUPS).flatMap(([groupId, aliases]) => aliases.map((alias) => [alias, groupId])),
);

/**
 * Löst eine Rohschreibweise ("m", "100 ST", "Kt.", …) auf ihre kanonische Alias-Gruppen-ID
 * auf, oder null, wenn unbekannt. Bewusst konservative, kleine Synonym-Tabelle — unbekannte
 * Einheiten bleiben unklassifiziert statt geraten zu werden (siehe "unbekannt ≠ Veto"
 * unten). Exportiert für Tests/Wiederverwendung durch die Übernahme-Logik.
 */
export function normalizeUnitClass(unit: string | null | undefined): string | null {
  if (!present(unit)) return null;
  const { rest } = parseLeadingQuantityPrefix(unit);
  const stripped = (rest ?? unit).trim().toLowerCase().replace(/\.$/, '');
  return UNIT_ALIAS_LOOKUP[stripped] ?? null;
}

/**
 * True, wenn BEIDE Einheiten bekannt sind UND NICHT derselben kanonischen Alias-Gruppe
 * angehören (siehe {@link normalizeUnitClass} und den P1-2-Modellwechsel-Kommentar oben).
 * "Unbekannt ≠ Veto": ist mindestens eine Seite unbekannt/leer, wird NICHT blockiert.
 */
export function areUnitsIncompatible(
  positionUnit: string | null | undefined,
  candidateUnit: string | null | undefined,
): boolean {
  const positionGroup = normalizeUnitClass(positionUnit);
  const candidateGroup = normalizeUnitClass(candidateUnit);
  if (!positionGroup || !candidateGroup) return false; // unbekannt ≠ Veto
  return positionGroup !== candidateGroup;
}

/**
 * P2-3 (Fund): effectiveUnitPrice() (shared/effective-unit-price.ts) vergleicht nur
 * candidate-interne Metadaten (das aus price_unit geparste unitLabel gegen quantityUnit) —
 * fehlt die Kandidaten-eigene Einheit (quantityUnit/candidateUnit), bleibt
 * `eff.unitsIncompatible` wirkungslos ("unbekannt ≠ Veto"), SELBST wenn price_unit
 * eindeutig eine andere Einheitenklasse verrät (z.B. "100 ST" = Stück) als die LV-Position
 * erwartet (z.B. "m"). shared/catalog-batch-suggest.ts::buildBatchCatalogRow prüft deshalb
 * zusätzlich eff.unitLabel als FALLBACK-Kandidateneinheit gegen die LV-Einheit, wenn die
 * Kandidaten-eigene Einheit fehlt/leer ist — dieselbe Logik hier EINMAL geteilt, damit der
 * Einzeldialog (client/src/components/kalkulation/catalog-suggest-dialog.tsx) sie ebenfalls
 * anwendet (Symmetrie zum Batch-Pfad; der Einzeldialog machte diesen Fallback bisher NICHT,
 * wodurch quantity_unit=null + price_unit="100 ST" gegen eine "m"-Position unerkannt blieb).
 */
export function unitsIncompatibleMitPriceUnitFallback(
  eff: { unitsIncompatible: boolean; unitLabel: string | null },
  candidateUnit: string | null | undefined,
  positionUnit: string | null | undefined,
): boolean {
  const kandidatEinheitBekannt = typeof candidateUnit === 'string' && candidateUnit.trim().length > 0;
  return eff.unitsIncompatible || (!kandidatEinheitBekannt && areUnitsIncompatible(positionUnit, eff.unitLabel));
}

// ============================================
// Main ranking function
// ============================================

/**
 * Rankt Katalog-Kandidaten für eine LV-Position.
 * Einheiten-inkompatible Kandidaten werden vor dem Scoring verworfen.
 * Sortiert absteigend nach confidence, begrenzt auf topN (Default 5).
 */
export function rankCatalogCandidates(
  position: PositionForCatalogMatch,
  candidates: CatalogArticleCandidate[],
  topN: number = DEFAULT_TOP_N,
): RankedCatalogCandidate[] {
  const normalizedInput = present(position.artikelnummer) ? normalizeCode(position.artikelnummer!) : null;

  const ranked: RankedCatalogCandidate[] = [];

  for (const candidate of candidates) {
    if (areUnitsIncompatible(position.einheit, candidate.unit)) {
      continue; // Veto: Einheiten-Klassen bekannt und unterschiedlich
    }

    // 1. Hartes Signal: Artikelnummer
    if (normalizedInput && present(candidate.articleNumber) && normalizeCode(candidate.articleNumber!) === normalizedInput) {
      ranked.push({ candidate, confidence: 1, zone: 'high', method: 'articleNumber' });
      continue;
    }

    // 2. Hartes Signal: EAN (Position trägt EAN ggf. im artikelnummer-Feld, z.B. Scan)
    if (normalizedInput && present(candidate.ean) && normalizeCode(candidate.ean!) === normalizedInput) {
      ranked.push({ candidate, confidence: 1, zone: 'high', method: 'ean' });
      continue;
    }

    // 3. Fuzzy-Fallback auf Beschreibungstext
    const confidence = fuzzyScore(position, candidate);
    ranked.push({ candidate, confidence, zone: zoneForConfidence(confidence), method: 'fuzzy' });
  }

  ranked.sort((a, b) => b.confidence - a.confidence);
  return ranked.slice(0, topN);
}

// ============================================
// Generation-/Sequenz-Guard (P1-5 / P2-7)
// ============================================

/**
 * True, wenn `startGeneration` (die Generation, MIT der eine asynchrone Katalog-Suche
 * gestartet wurde) noch der aktuellen Generation entspricht — false, wenn zwischenzeitlich
 * eine neuere Suche gestartet wurde (Dialog neu geöffnet, Angebot gewechselt) ODER der
 * Dialog geschlossen wurde (beides erhöht den Generation-Zähler). Pure Vergleichsfunktion,
 * damit sie ohne echten Timer-/Promise-Ablauf unter node:test geprüft werden kann — der
 * Generation-Zähler selbst lebt als `useRef` in der jeweiligen Dialog-Komponente
 * (client/src/components/kalkulation/catalog-suggest-dialog.tsx und
 * catalog-batch-suggest-dialog.tsx).
 *
 * Fund (P1-5, Batch-Dialog): Dialog schließen/neu öffnen oder ein Angebotswechsel während
 * eine Batch-Suche läuft — die ältere Invocation kann zuletzt fertig werden und neuere
 * Zeilen mit altem `kalkulationBestehend` überschreiben, wodurch eine zwischenzeitlich
 * editierte Kalkulation verloren ginge.
 * Fund (P2-7, Einzel-Dialog): zwei überlappende Suchen — die ältere Antwort löst zuletzt
 * auf und ersetzt neuere Ergebnisse; ein altes `finally` kann außerdem den Loading-State
 * löschen, während der neue Request noch läuft.
 */
export function istAktuelleGeneration(startGeneration: number, currentGeneration: number): boolean {
  return startGeneration === currentGeneration;
}
